import { Box, HStack } from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'
import { useUserState } from '~/atoms/UserState'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { shouldUseHotelsExperience } from '~/utils/pbxUtils'

import { HotelsSubNavLink } from './useHotelsPageContext'
import { useHotelsPermissions } from './useHotelsPermissions'

interface HotelsLayoutProps {
  children: ReactNode
}

export const HotelsLayout = ({ children }: HotelsLayoutProps) => {
  const { t } = useTranslation()
  const { isReady, pbx } = useUserState({ canAutoRefresh: false })
  const { permissions, hasAnyAccess } = useHotelsPermissions()

  if (!isReady) {
    return null
  }

  if (!shouldUseHotelsExperience({ pbx }) || !hasAnyAccess) {
    return <Navigate to={ApplicationRoutes.CALLS} replace />
  }

  return (
    <Box
      w="full"
      minW={0}
      flex={1}
      h="full"
      minH={0}
      overflowY="auto"
      overflowX="hidden"
      bg="bg.default"
    >
      <HStack
        gap={1}
        pb={4}
        borderBottomWidth={1}
        borderColor="border.default"
        mb={4}
        px={6}
        pt={6}
      >
        {permissions.canViewDashboard && (
          <HotelsSubNavLink
            to={ApplicationRoutes.HOTELS}
            label={t('hotels.nav.dashboard', 'Dashboard')}
            end
          />
        )}
        {permissions.canViewSettings && (
          <HotelsSubNavLink
            to={ApplicationRoutes.HOTELS_SETTINGS}
            label={t('hotels.nav.settings', 'Settings')}
          />
        )}
        {permissions.canViewScheduler && (
          <HotelsSubNavLink
            to={ApplicationRoutes.HOTELS_WAKEUP}
            label={t('hotels.nav.wakeupScheduling', 'Wake-Up Scheduling')}
          />
        )}
        {permissions.canViewHistory && (
          <HotelsSubNavLink
            to={ApplicationRoutes.HOTELS_WAKEUP_HISTORY}
            label={t('hotels.nav.wakeupHistory', 'Wake-Up History')}
          />
        )}
      </HStack>

      <Box px={6} pb={6}>
        {children}
      </Box>
    </Box>
  )
}
